import dayjs from 'dayjs';
import camelCase from 'lodash/camelCase';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import { isArray } from 'lodash/lang';
import { transform } from 'lodash/object';

import {
  SENTIMENT_OPTIONS,
  SOCIAL_LISTENING_DATE_OPTIONS,
  SOCIAL_LISTENING_KEYWORD_TYPE_OPTIONS,
  SOCIAL_LISTENING_SENTIMENTS_OPTIONS,
} from '@/app/socialListening/constants';
import { TRENDS_DEFAULT_FILTERS } from '@/app/socialListening/constants/listeningFilters';
import { getDateRangeByOffset } from '@/app/socialListening/utils/filter-utils';
import { getUserTimezone } from '@/utils/timezone';

export function getStartAndEndDate(dateRange, dateOptions = SOCIAL_LISTENING_DATE_OPTIONS) {
  let startDate = null;
  let endDate = null;
  const subtractUnit = dateRange && dateOptions ? dateOptions[dateRange]?.subtractUnit : null;
  const subtractValue = dateRange && dateOptions ? dateOptions[dateRange]?.subtractValue : null;
  if (subtractUnit && subtractValue) {
    if (subtractUnit === 'day' && subtractValue === 1) {
      endDate = dayjs().set('minute', 0).set('second', 0);
      startDate = endDate.subtract(subtractValue, subtractUnit);
    } else {
      endDate = dayjs().endOf('day');
      // The expected time range should always include the entire day today
      // To avoid miscalculations, we subtract from the beginning of tomorrow
      startDate = dayjs().add(1, 'day').startOf('day');
      startDate = startDate.subtract(subtractValue, subtractUnit);
    }
    endDate = endDate.format('YYYY-MM-DDTHH:mm:ss');
    startDate = startDate.format('YYYY-MM-DDTHH:mm:ss');
  }
  return { startDate, endDate };
}

export function sortObjectKeys(obj) {
  return Object.keys(obj)
    .sort()
    .reduce((acc, key) => {
      acc[key] = obj[key];
      return acc;
    }, {});
}

export function getBackendFilterPayload(
  dateRange,
  sentimentList,
  channelList,
  mediaTypeList,
  keywords,
  dateOptions,
  startDate = null,
  endDate = null,
  visualData = null,
  industryIds = null,
  competitorIds = null,
) {
  const {
    channels: defaultChannels,
    sentiments: defaultSentiments,
    mediaTypes: defaultMediaTypes,
  } = TRENDS_DEFAULT_FILTERS;

  const filterPayload = {
    sources: isEmpty(channelList) ? defaultChannels : channelList,
    media_types: {
      includes: isEmpty(mediaTypeList) ? defaultMediaTypes : mediaTypeList,
    },
  };

  const sentiments = isEmpty(sentimentList) ? defaultSentiments : sentimentList;
  const sentimentSet = new Set(sentiments);
  const isAllSentiments = isEqual(sentimentSet, SENTIMENT_OPTIONS);

  if (!isAllSentiments) {
    const includePositive = (sentimentList ?? []).includes(
      SOCIAL_LISTENING_SENTIMENTS_OPTIONS.POSITIVE.value,
    );
    const includeNeutral = (sentimentList ?? []).includes(
      SOCIAL_LISTENING_SENTIMENTS_OPTIONS.NEUTRAL.value,
    );
    const includeNegative = (sentimentList ?? []).includes(
      SOCIAL_LISTENING_SENTIMENTS_OPTIONS.NEGATIVE.value,
    );

    filterPayload.sentiment = {
      include_positive: includePositive,
      include_neutral: includeNeutral,
      include_negative: includeNegative,
    };
  }
  let rangeStart;
  let rangeEnd;
  // if fixed dates not provided, use date option selected
  if (!startDate) {
    const rangeDates = getStartAndEndDate(dateRange, dateOptions);
    rangeStart = rangeDates.startDate;
    rangeEnd = rangeDates.endDate;
  } else {
    rangeStart = startDate;
    rangeEnd = endDate;
  }
  if (rangeStart) {
    filterPayload.source_created = {
      on_or_after: rangeStart,
    };
  }
  if (rangeEnd) {
    filterPayload.source_created.on_or_before = rangeEnd;
  }

  if (keywords && keywords.length > 0) {
    filterPayload.keywords_and_hashtags = keywords;
  }

  if (visualData) {
    filterPayload.visual_data = visualData;
  }

  if (industryIds) {
    filterPayload.industry_ids = industryIds;
  }

  if (competitorIds) {
    filterPayload.source_and_creator_ids = competitorIds;
  }

  return sortObjectKeys(filterPayload);
}

export function getKeywordORString(filters, topKeywords) {
  if (filters.keyword) {
    return filters.keyword;
  }
  const keywordList = topKeywords.map((keywordData) => keywordData.keyword);
  return keywordList.join(' OR ');
}

export function camelCaseObjDeep(snakeObject) {
  return transform(snakeObject, (acc, value, key, target) => {
    const camelKey = isArray(target) ? key : camelCase(key);
    acc[camelKey] = isObject(value) ? camelCaseObjDeep(value) : value;
  });
}

export function getKeywordTypePayload(keywordTypes) {
  const selected = isEmpty(keywordTypes) ? TRENDS_DEFAULT_FILTERS.keywordTypes : keywordTypes;
  const { KEYWORD, HASHTAG } = SOCIAL_LISTENING_KEYWORD_TYPE_OPTIONS;
  return {
    includeKeywords: selected.includes(KEYWORD.value),
    includeHashtags: selected.includes(HASHTAG.value),
  };
}

export function getAnomalyDateRange(topic) {
  const { sourceCreated } = topic.searchBody.filters;
  let { filtersLastUpdatedAt } = topic;
  const dateFormat = 'YYYY-MM-DDTHH:mm:ss';
  let { onOrAfter, onOrBefore } = sourceCreated;
  const { rollingDateRangeOffset } = sourceCreated;
  let startDate;
  let endDate;

  if (rollingDateRangeOffset) {
    const { start, end } = getDateRangeByOffset(rollingDateRangeOffset);
    onOrAfter = start;
    onOrBefore = end;
  }

  if (!filtersLastUpdatedAt) {
    startDate = onOrAfter;
    endDate = onOrBefore;
  } else {
    // filtersLastUpdatedAt is received as UTC, so we need to convert it before
    // using it in the request to get topic monitor events
    filtersLastUpdatedAt = dayjs.utc(filtersLastUpdatedAt).tz(getUserTimezone()).format(dateFormat);
    startDate = dayjs(onOrAfter).isAfter(filtersLastUpdatedAt) ? onOrAfter : filtersLastUpdatedAt;
    endDate = dayjs(onOrBefore).isAfter(filtersLastUpdatedAt) ? onOrBefore : filtersLastUpdatedAt;
  }
  startDate = dayjs(startDate).format(dateFormat);
  endDate = dayjs(endDate).format(dateFormat);
  return { startDate, endDate };
}
